import { Button, Form, Input, List, Upload, Skeleton } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { PaperClipOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import { useAlert } from "../../context/AlertProvider";
import { EmailContext } from "../../context/EmailContext";
import CopyText from "../../components/web2lead/CopyText";
import copy from "../../assets/images/webToLead/copy.png";
import { sendEmail } from "../../actionCreator/emailSetupActionCreater";
import { getUserInfo } from "../../utils/utils";
import generateRes from "../../assets/svg/generate-response.svg";
import {
  clearMessages,
  generateEmailAI,
  createAISession,
  appendChatMessage,
} from "../../actionCreator/genAIActionCreator";
import { ReactComponent as SendIcon } from "../../assets/svg/icons/send-email.svg";
import { ReactComponent as Rewrite } from "../../assets/svg/icons/rewrite.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/webToLead/delete.svg";
import { useEmailWebSocket } from "../../common/hooks/useEmailWebSocket";
import Editor from "../../common/Editor";

const SidePanelEmailComposer = ({
  recipientEmail,
  empId,
  payload,
  callback,
  convertToLead,
  onConvertToLead,
  handleReset,
  selectedDraft,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const editorRef = useRef(null);
  const { subscribeToSession, unsubscribeFromSession } =
    useEmailWebSocket("email-channel-1");
  const { showAlert } = useAlert();
  const { emailConfiguration } = useContext(EmailContext);
  const [fileList, setFileList] = useState([]);
  const [embeddedImgs, setEmbeddedImgs] = useState([]);
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStreamRaw, setAiStreamRaw] = useState("");
  const [aiLive, setAiLive] = useState({ subject: "", body: "" });
  const [isStreamingStopped, setIsStreamingStopped] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [showAiPromptBox, setShowAiPromptBox] = useState(false);
  const [hasBodyError, setHasBodyError] = useState(false);
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const skeletonTimeoutRef = useRef(null);
  const streamMessages = useSelector(
    (state) => state.genAIReducer.streamMessages,
  );
  const isLoading = useSelector((state) => state.genAIReducer.loading);

  const handleCancel = () => {
    handleReset();
    form.resetFields();
    setError();
    setFileList([]);
    setEmbeddedImgs([]);
    setAiStreamRaw("");
    setAiLive({ subject: "", body: "" });
    setIsStreamingStopped(false);
    setAiPrompt("");
    setShowAiPromptBox(false);
    setHasBodyError(false);
    setSkeletonLoading(false);
    if (skeletonTimeoutRef.current) {
      clearTimeout(skeletonTimeoutRef.current);
      skeletonTimeoutRef.current = null;
    }
    dispatch(clearMessages());
    unsubscribeFromSession();
  };

  const handleFilesChange = ({ file, fileList }) => {
    const isLt10MB = file.size / 1024 / 1024 < 10;
    if (!isLt10MB) {
      showAlert("error", `${file.name} is larger than 10MB!`);
    } else {
      setFileList(fileList);
    }
  };

  const handleRemove = (file) => {
    setFileList((prevList) => prevList.filter((item) => item.uid !== file.uid));
  };

  useEffect(() => {
    if (emailConfiguration?.email || recipientEmail) {
      form.setFieldsValue({
        from: emailConfiguration?.email,
        to: recipientEmail,
      });
    }
  }, [emailConfiguration, recipientEmail, form]);

  useEffect(() => {
    if (payload?.subject) {
      form.setFieldsValue({ subject: payload?.subject });
    }
  }, [payload, form]);

  useEffect(() => {
    if (editorRef.current && selectedDraft) {
      editorRef.current.setTemplate(fixHtml(selectedDraft?.body));
    }
  }, [selectedDraft, editorRef]);

  const extractAIFieldsFromString = (raw) => {
    if (!raw) return { subject: "", body: "" };
    const subjMatch = raw.match(/<subject>([\s\S]*?)<\/subject>/i);
    const subject = subjMatch ? subjMatch[1].trim() : "";
    const body = subjMatch
      ? raw.replace(/[\s\S]*<\/subject>/i, "").trim()
      : raw.trim();
    return { subject, body };
  };

  useEffect(() => {
    if (!streamMessages) return;

    // stop skeleton loader once we have streaming content
    if (skeletonTimeoutRef.current) {
      clearTimeout(skeletonTimeoutRef.current);
      skeletonTimeoutRef.current = null;
      setSkeletonLoading(false);
    }

    const combined = Array.isArray(streamMessages)
      ? streamMessages.join("")
      : String(streamMessages);

    if (isStreamingStopped) return;

    setAiStreamRaw(combined);

    const parsed = extractAIFieldsFromString(combined);

    setAiLive((prev) => ({
      subject: parsed.subject || prev.subject,
      body: parsed.body || prev.body,
    }));
  }, [streamMessages, isStreamingStopped]);

  useEffect(() => {
    if (!isLoading && aiStreamRaw) {
      const parsed = extractAIFieldsFromString(aiStreamRaw);

      setAiLive({
        subject: parsed.subject || aiLive.subject,
        body: parsed.body || aiLive.body,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handlePromptBasedAISuggestion = async () => {
    setShowAiPromptBox(true);
    setError();
    if (!aiPrompt.trim()) {
      showAlert("warning", "Please enter a prompt");
      return;
    }

    try {
      setError();
      setAiLoading(true);
      setSkeletonLoading(true);
      dispatch(clearMessages());
      setAiStreamRaw("");
      setAiLive({ subject: "", body: "" });
      setIsStreamingStopped(false);

      // skeleton fallback (max 12 seconds)
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
      }
      skeletonTimeoutRef.current = setTimeout(() => {
        setIsStreamingStopped(true);
        unsubscribeFromSession();
        setAiLoading(false);
        setSkeletonLoading(false);
        showAlert(
          "warning",
          "AI took too long to respond. Try again or refine your prompt.",
        );
      }, 12000);

      const res = await dispatch(createAISession());
      const sessionId = res?.data?.sessionId;

      if (!sessionId) {
        showAlert("error", "Unable to create AI session.");
        setAiLoading(false);
        setSkeletonLoading(false);
        if (skeletonTimeoutRef.current) {
          clearTimeout(skeletonTimeoutRef.current);
          skeletonTimeoutRef.current = null;
        }
        return;
      }

      await subscribeToSession(sessionId);

      const user = getUserInfo();
      const payloadObj = {
        sessionId,
        userInput: aiPrompt,
        senderFullName: user?.firstName + " " + user?.lastName,
        lead: {
          firstName: payload?.firstname,
          lastName: payload?.lastname,
          title: payload?.title,
          companyName: payload?.companyName,
        },
      };

      await dispatch(generateEmailAI(payloadObj));
    } catch (err) {
      console.error(err);
      showAlert("error", "AI generation failed");
      setAiLoading(false);
      setSkeletonLoading(false);
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
        skeletonTimeoutRef.current = null;
      }
    } finally {
      setAiLoading(false);
    }
  };

  const addSrcToImages = (htmlString, imagesArray) => {
    return htmlString.replace(
      /<img\s+([^>]*data-ck-upload-id="([^"]+)")/g,
      (match, fullAttribute, uploadId) => {
        const image = imagesArray.find((img) => img.id === uploadId);
        if (image) {
          return `<img ${fullAttribute} src="${image.cidUrl}"`;
        }
        return match;
      },
    );
  };

  const onSubmit = async (values) => {
    setLoading(true);

    const finalSubject = aiLive.subject || values.subject;

    const finalBody = aiLive.body || values?.body;

    const finalMailBody = addSrcToImages(finalBody, embeddedImgs);

    const data = new FormData();

    data.append(
      "request",
      JSON.stringify({
        from: values.from,
        to: values.to,
        subject: finalSubject,
        body: finalMailBody,
        ...payload,
        context: { employee_id: empId },
      }),
    );

    fileList.forEach((file) => {
      data.append("attachments", file.originFileObj);
    });

    embeddedImgs.forEach((img) => {
      if (finalMailBody.includes(img.id)) {
        data.append("inlineAttachments", img.file);
      }
    });

    let res;

    if (convertToLead) {
      await onConvertToLead();
      res = await dispatch(sendEmail(data));
    } else {
      res = await dispatch(sendEmail(data));
    }

    if (res?.status === 200) {
      showAlert(
        "success",
        convertToLead
          ? "The email has been sent and a lead has been created successfully."
          : "Email sent successfully!",
      );
      dispatch(
        appendChatMessage({
          message: `Email sent to ${recipientEmail} successfully!`,
          stage: "SUCCESS",
          type: "EMAIL_SUCCESS",
        }),
      );
      handleCancel();
      callback && callback();
    } else {
      const errMsg =
        res?.response?.status === 500
          ? "Something went wrong. Please try again."
          : res?.response?.status === 403
            ? "We could not send your email because the necessary permission was not granted. Please go back and make sure to check the box to allow email access when reconnecting your account."
            : res?.response?.data?.title;
      showAlert("error", errMsg);
      setError(errMsg);
    }

    setLoading(false);
  };

  useEffect(() => {
    return () => {
      dispatch(clearMessages());
      unsubscribeFromSession();
      if (skeletonTimeoutRef.current) {
        clearTimeout(skeletonTimeoutRef.current);
        skeletonTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const fixHtml = (html) => {
    if (!html) return "";
    return html
      .replace(/\n/g, "<br/>")
      .replace(/ {2,}/g, (m) => "&nbsp;".repeat(m.length));
  };

  const handleStopStreaming = () => {
    setIsStreamingStopped(true);
    unsubscribeFromSession();
    if (skeletonTimeoutRef.current) {
      clearTimeout(skeletonTimeoutRef.current);
      skeletonTimeoutRef.current = null;
    }
    setSkeletonLoading(false);
  };

  const applySuggestionsToForm = () => {
    form.setFieldsValue({
      subject: aiLive.subject,
      body: fixHtml(aiLive.body),
    });

    if (editorRef.current) {
      editorRef.current.setTemplate(fixHtml(aiLive.body));
    }
    setAiStreamRaw("");
    setAiLive({ subject: "", body: "" });
    dispatch(clearMessages());
    unsubscribeFromSession();
    if (skeletonTimeoutRef.current) {
      clearTimeout(skeletonTimeoutRef.current);
      skeletonTimeoutRef.current = null;
    }
    setSkeletonLoading(false);
  };
  const hasAISuggestion = !!(aiLive.body || aiLive.subject);
  const isAiGenerating = skeletonLoading || isLoading;

  return (
    <Form
      form={form}
      layout="vertical"
      className="overflow-x-hidden rounded tw-pt-2 heading13px d-flex flex-column justify-content-between"
      style={{ maxWidth: 650, height: 550 }}
      onFinish={onSubmit}
      onFieldsChange={(_, allFields) => {
        const bodyField = allFields?.find(
          (field) => field?.name?.[0] === "body",
        );
        setHasBodyError(!!bodyField?.errors?.length);
      }}
    >
      <div style={{ height: 550, overflowY: "auto" }}>
        {!!error && (
          <p className="text-error text-center pb-2 px-2 mx-2">{error}</p>
        )}

        <div className="px-2">
          <div className="tw-hidden align-items-center mt5 tw-mb-3">
            <label className="heading13px">From:</label>
            <Form.Item
              name="from"
              rules={[
                {
                  type: "email",
                  message: "Please enter valid E-mail!",
                },
                { required: true, message: "From is required" },
              ]}
              className="mb0"
              style={{ width: "100%" }}
            >
              <Input
                disabled
                defaultValue={emailConfiguration?.email}
                variant="borderless"
                style={{ width: "100%" }}
                className="text-color-030334"
              />
            </Form.Item>
            <div style={{ marginRight: 30 }}>
              <CopyText
                text={emailConfiguration?.email}
                content={
                  <img
                    src={copy}
                    alt=""
                    style={{ width: 15, height: 15 }}
                    className="cursor-pointer"
                  />
                }
              />
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-3 mt5 tw-mb-2">
            <label className="heading13px">To:</label>
            <Form.Item
              name="to"
              className="mb0"
              rules={[
                {
                  type: "email",
                  message: "Please enter valid E-mail!",
                },
                { required: true, message: "Recipient is required" },
              ]}
            >
              <Input
                disabled
                defaultValue={recipientEmail}
                className="text-color-030334"
              />
            </Form.Item>
          </div>

          <div className="d-flex align-items-center mt5 tw-mb-4">
            <label className="heading13px">Subject:</label>

            <Form.Item
              name="subject"
              rules={[{ required: true, message: "Subject is required" }]}
              className="mb0"
              style={{ width: "100%" }}
            >
              <Input
                disabled={payload?.subject}
                variant="borderless"
                style={{ width: "100%" }}
                className="text-color-030334 tw-font-semibold"
              />
            </Form.Item>
          </div>

          {showAiPromptBox && (
            <div
              style={{ height: "350px", overflowY: "auto" }}
              className="mt20"
            >
              {!isAiGenerating && !hasAISuggestion && (
                <div>
                  <Input.TextArea
                    placeholder="Ask AI to write your email..."
                    value={aiPrompt}
                    onChange={(e) => {
                      setAiPrompt(e.target.value);
                      setAiStreamRaw("");
                    }}
                    rows={10}
                    className="tw-rounded-md tw-border-[#9787FE]"
                    style={{
                      background:
                        "linear-gradient(180deg, #F2F0FF 0%, #FFFFFF 50%, #F2F0FF 100%)",
                    }}
                    onPressEnter={handlePromptBasedAISuggestion}
                  />
                  <div className="tw-text-end tw-mt-2">
                    <Button
                      type="primary"
                      className="!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-xs"
                      loading={aiLoading}
                      disabled={!aiPrompt?.trim().length}
                      onClick={handlePromptBasedAISuggestion}
                    >
                      <img
                        src={generateRes}
                        alt=""
                        style={{ width: 16, height: 16 }}
                      />
                      Generate Email
                    </Button>
                  </div>
                </div>
              )}

              {skeletonLoading && !aiLive.body && (
                <div className="d-flex flex-column gap-10">
                  <Skeleton.Input
                    active
                    block
                    style={{ height: 20, width: "100%" }}
                  />
                  <Skeleton.Input
                    active
                    block
                    style={{ height: 20, width: "100%" }}
                  />
                  <Skeleton.Input
                    active
                    block
                    style={{ height: 20, width: "100%" }}
                  />
                  <Skeleton.Input
                    active
                    block
                    style={{ height: 20, width: "100%" }}
                  />
                  <Skeleton.Input
                    active
                    block
                    style={{ height: 20, width: "100%" }}
                  />
                  <Skeleton.Input
                    active
                    block
                    style={{ height: 20, width: "100%" }}
                  />
                  <Skeleton.Input
                    active
                    block
                    style={{ height: 20, width: "100%" }}
                  />
                </div>
              )}

              {isLoading && aiStreamRaw && !aiLive.body && (
                <div className="p10 mb20 card px-10 py-10">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="heading16px fw-semibold">
                      AI (Streaming...)
                    </div>
                    <div>
                      <Button size="small" onClick={handleStopStreaming}>
                        Stop
                      </Button>
                    </div>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    {aiLive.subject ? (
                      <div className="fw-semibold mb6">{aiLive.subject}</div>
                    ) : (
                      <div className="text-muted mb6">Generating subject…</div>
                    )}

                    <div
                      style={{
                        maxHeight: 220,
                        overflowY: "auto",
                        borderRadius: 4,
                        padding: 8,
                        background: "#fafafa",
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        lineHeight: "1.5",
                      }}
                    >
                      {(aiLive.body || aiStreamRaw)
                        .split("\n")
                        .map((line, index) => (
                          <div key={index}>{line}</div>
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {!isLoading && (aiLive.body || aiLive.subject) && (
                <>
                  <div
                    style={{
                      maxHeight: "330px",
                      overflowY: "auto",
                      background:
                        "linear-gradient(180deg, #F2F0FF 0%, #FFFFFF 50%, #F2F0FF 100%)",
                    }}
                    className="p10 mb20 px-10 py-10 tw-border tw-border-theme tw-border-solid tw-rounded-[10px]"
                  >
                    <div className="heading16px fw-semibold mt10">
                      {aiLive.subject || "Suggested subject"}
                    </div>

                    <div
                      dangerouslySetInnerHTML={{
                        __html: fixHtml(aiLive.body || ""),
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className={`${!!showAiPromptBox && "d-none"} tw-relative tw-px-3`}>
          <Form.Item
            name="body"
            rules={[{ required: true, message: "Content is required" }]}
            className="mt5 mb0 email-content-error"
          >
            <Editor
              ref={editorRef}
              setEmbeddedImgs={setEmbeddedImgs}
              onChange={(data) => form.setFieldsValue({ body: data })}
            />
          </Form.Item>

          {!showAiPromptBox && (
            <div
              className={`tw-absolute tw-right-8 ${hasBodyError ? "tw-bottom-[2.5rem]" : "tw-bottom-[1.25rem]"} tw-z-10`}
            >
              <Button
                type="default"
                className="tw-text-theme !tw-bg-[#EFEDFF] tw-border-theme hover:!tw-border-theme hover:!tw-text-theme tw-font-semibold tw-text-sm"
                onClick={() => {
                  setShowAiPromptBox(true);
                  setAiStreamRaw("");
                  setAiLive({ subject: "", body: "" });
                  dispatch(clearMessages());
                }}
              >
                <Rewrite />
                Generate with AI
              </Button>
            </div>
          )}
        </div>
        {convertToLead && (
          <div className="tw-p-3">
            <b>Note:</b> Sending this email will automatically create a lead in
            the system.
          </div>
        )}

        {!!fileList?.length && (
          <div className="px-2">
            <List
              size="small"
              className="attachment-list mt10"
              dataSource={fileList}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <DeleteIcon
                      key="delete"
                      onClick={() => handleRemove(item)}
                      style={{ color: "red", cursor: "pointer" }}
                    />,
                  ]}
                  style={{ padding: "8px 0" }}
                >
                  {item.name}
                </List.Item>
              )}
            />
          </div>
        )}
      </div>

      {showAiPromptBox && !isLoading && hasAISuggestion && (
        <div
          style={{
            background:
              "linear-gradient(180deg, #F2F0FF 0%, #FFFFFF 50%, #F2F0FF 100%)",
          }}
          className="p10 mb20 tw-mx-4 px-10 py-10 tw-border tw-flex tw-items-center tw-justify-between tw-border-theme tw-border-solid tw-rounded-[10px]"
        >
          <Button
            type="text"
            className="tw-text-xs tw-font-bold tw-text-sub-text"
            onClick={() => {
              setAiStreamRaw("");
              setAiLive({ subject: "", body: "" });
              dispatch(clearMessages());
              setShowAiPromptBox(false);
              setAiPrompt();
            }}
          >
            <Rewrite /> Rewrite
          </Button>
          <div className="tw-flex tw-gap-2">
            <Button
              type="text"
              className="tw-text-xs tw-font-bold tw-text-sub-text"
              onClick={() => {
                setAiStreamRaw("");
                setAiLive({ subject: "", body: "" });
                dispatch(clearMessages());
                setShowAiPromptBox(false);
                setAiPrompt();
              }}
            >
              Discard
            </Button>
            <Button
              type="primary"
              className="!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
              disabled={skeletonLoading && !aiLive.body}
              onClick={() => {
                applySuggestionsToForm();
                setShowAiPromptBox(false);
                setAiPrompt();
              }}
            >
              Insert
            </Button>
          </div>
        </div>
      )}
      {!(showAiPromptBox && !isLoading && hasAISuggestion) && (
        <div className="tw-bg-secondary-color tw-py-4 tw-px-5">
          <div className="tw-flex tw-gap-2 tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Upload
                multiple
                fileList={fileList}
                beforeUpload={() => false}
                onChange={handleFilesChange}
                showUploadList={false}
              >
                <PaperClipOutlined className="tw-text-md cursor-pointer tw-text-sub-text" />
              </Upload>
              <div className="tw-text-sm tw-text-sub-text">Max. 10 MB</div>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Button
                loading={loading}
                type="text"
                className="tw-text-text-primary tw-font-semibold"
                onClick={() => {
                  if (showAiPromptBox) {
                    setShowAiPromptBox(false);
                  } else {
                    handleCancel();
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                loading={loading}
                type="primary"
                htmlType="submit"
                disabled={showAiPromptBox}
                className="!tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
              >
                <SendIcon />
                Send Email
              </Button>
            </div>
          </div>
        </div>
      )}
    </Form>
  );
};

export default SidePanelEmailComposer;
